/**
 * File Access Gateway
 *
 * Unified file-system access layer for the browser runtime.
 *
 * Strategy:
 * - Chromium-based browsers expose the File System Access API, which opens
 *   the OPERATING SYSTEM's native Open/Save dialogs and allows real
 *   read/write access to files on disk (no download round-trips).
 * - Browsers without the API (Firefox, Safari) fall back to the in-app file
 *   browser over the virtual FS (virtualFs.ts) for both reads and writes;
 *   the Blob download in this module remains the last-resort save path.
 */

export const PROJECT_FILE_EXTENSION = 'yap';
export const TEMPLATE_FILE_EXTENSION = 'yate';

/** Accept-list shared by every Open/Save picker in the application. Open
 *  also admits native CAD + recovery extensions (Faz 9.3): .dxf/.dwg open
 *  through the native-import pipeline, .dwt/.dws as templates, .bak/.sv$
 *  are sniffed from their leading bytes. Save stays document-only. */
export const DOCUMENT_FILE_TYPES: { description: string; accept: Record<string, string[]> }[] = [
    { description: 'Yap Project', accept: { 'application/json': [`.${PROJECT_FILE_EXTENSION}`] } },
    { description: 'Yap Project Template', accept: { 'application/json': [`.${TEMPLATE_FILE_EXTENSION}`] } },
    { description: 'Yap Recovery (.bak, .sv$)', accept: { 'application/octet-stream': ['.bak', '.sv$'] } },
    { description: 'DXF Drawing', accept: { 'application/dxf': ['.dxf'], 'application/octet-stream': ['.dxf'] } },
    { description: 'DWG Drawing', accept: { 'application/dwg': ['.dwg'], 'application/octet-stream': ['.dwg'] } },
    { description: 'DWG Template/Standard (.dwt, .dws)', accept: { 'application/dwg': ['.dwt', '.dws'], 'application/octet-stream': ['.dwt', '.dws'] } },
];

/** Save pickers stay document-only (DWG write is deliberately not offered
 *  — see docs/reports/file-io/2026-10-16-dwg-write-feasibility.md, Faz 9.10). */
export const SAVE_FILE_TYPES = DOCUMENT_FILE_TYPES.slice(0, 2);

/** Minimal local typings for the File System Access API (not in lib.dom). */
interface OpenFilePickerOptions {
    types?: { description: string; accept: Record<string, string[]> }[];
    excludeAcceptAllOption?: boolean;
    multiple?: boolean;
}
interface SaveFilePickerOptions {
    suggestedName?: string;
    types?: { description: string; accept: Record<string, string[]> }[];
}

declare global {
    interface Window {
        showOpenFilePicker?: (options?: OpenFilePickerOptions) => Promise<FileSystemFileHandle[]>;
        showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;
    }
}

/** True when the runtime supports native OS file dialogs with write access. */
export function isFileSystemAccessSupported(): boolean {
    return typeof window !== 'undefined' && typeof window.showOpenFilePicker === 'function';
}

/**
 * Opens the native OS Open dialog and returns the selected file handle,
 * or null when the user cancels.
 */
export async function pickFileToOpen(): Promise<FileSystemFileHandle | null> {
    if (!isFileSystemAccessSupported()) return null;
    try {
        const [handle] = await window.showOpenFilePicker!({
            types: DOCUMENT_FILE_TYPES,
            excludeAcceptAllOption: false,
            multiple: false,
        });
        return handle ?? null;
    } catch (e) {
        if (isAbortError(e)) return null;
        throw e;
    }
}

/**
 * Opens the native OS Save dialog and returns the chosen file handle,
 * or null when the user cancels. `types` overrides the accept list for
 * non-document exports (e.g. .dxf).
 */
/** Save pickers use the document-only accept list by default. */
export async function pickFileToSave(
    suggestedName: string,
    types: { description: string; accept: Record<string, string[]> }[] = SAVE_FILE_TYPES
): Promise<FileSystemFileHandle | null> {
    if (!isFileSystemAccessSupported()) return null;
    try {
        return await window.showSaveFilePicker!({
            suggestedName,
            types,
        });
    } catch (e) {
        if (isAbortError(e)) return null;
        throw e;
    }
}

/** Reads the full text content of a file handle. */
export async function readHandleText(handle: FileSystemFileHandle): Promise<string> {
    const file = await handle.getFile();
    return file.text();
}

/** Writes (replaces) the full text content of a file handle. */
export async function writeHandleText(handle: FileSystemFileHandle, contents: string): Promise<void> {
    const writable = await handle.createWritable();
    await writable.write(contents);
    await writable.close();
}

/** Reads the full binary content of a file handle (DWG/DXF sniffing). */
export async function readHandleBinary(handle: FileSystemFileHandle): Promise<Uint8Array> {
    const file = await handle.getFile();
    return new Uint8Array(await file.arrayBuffer());
}

/** Reads the whole File of a handle (native import flows need the File). */
export async function handleToFile(handle: FileSystemFileHandle): Promise<File> {
    return handle.getFile();
}

/**
 * Picks a single arbitrary file through a hidden input — used by command
 * runners that attach content files (.pdf, .csv point clouds, .pgp) where
 * the OS-native picker is unavailable (interaction steps only pick entities).
 */
export function pickLocalFile(accept: string): Promise<File | null> {
    return new Promise((resolve) => {
        if (typeof document === 'undefined') {
            resolve(null);
            return;
        }
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.style.display = 'none';
        input.addEventListener('change', () => {
            const file = input.files?.[0] ?? null;
            input.remove();
            resolve(file);
        });
        document.body.appendChild(input);
        input.click();
    });
}

/** File name without its extension — used for document display names. */
export function stripExtension(fileName: string): string {
    return fileName.replace(/\.(yap|yate|json|dxf|dwg|dwt|dws|bak|sv\$)$/i, '');
}

/** Writes (replaces) the full binary content of a file handle (PDF export). */
export async function writeHandleBinary(handle: FileSystemFileHandle, bytes: ArrayBuffer | Uint8Array): Promise<void> {
    const writable = await handle.createWritable();
    await writable.write(bytes instanceof Uint8Array ? bytes.slice().buffer : bytes);
    await writable.close();
}

/** Triggers a browser download for the given text content (last-resort save). */
export function downloadTextFile(fileName: string, contents: string, mime = 'application/json'): void {
    const blob = new Blob([contents], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/** Triggers a browser download for binary content (last-resort PDF save). */
export function downloadBinaryFile(fileName: string, bytes: Uint8Array, mime = 'application/pdf'): void {
    const blob = new Blob([bytes.slice().buffer], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function isAbortError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'AbortError';
}
